import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { schedulerService } from '../../../core/services/schedulerService';
import styles from './SchedulerSetting.module.scss';
import {
	JobCode,
	SchedulerDepartment,
	SchedulerPosition,
	getJobCodes,
	getDepartments,
	getPositions,
} from '../../../core/API/scheduler';
import { TimeOffReason } from '../../../types';
import { GeneralSettings } from '../../../types/scheduler';
import JobCodesList from './SchedulerSetting/JobCodesList';
import TimeOffReasonsList from './SchedulerSetting/TimeOffReasonsList';
import DepartmentsList from './SchedulerSetting/DepartmentsList';
import PositionsList from './SchedulerSetting/PositionsList';
import CreateJobCode from './SchedulerSetting/CreateJobCode';
import CreateTimeOffReason from './SchedulerSetting/CreateTimeOffReason';
import CreateDepartment from './SchedulerSetting/CreateDepartment';
import CreatePosition from './SchedulerSetting/CreatePosition';
import EditJobCode from './SchedulerSetting/EditJobCode';
import EditTimeOffReason from './SchedulerSetting/EditTimeOffReason';
import EditDepartment from './SchedulerSetting/EditDepartment';
import EditPosition from './SchedulerSetting/EditPosition';
import EditGeneralSettings from './SchedulerSetting/EditGeneralSettings';
import { formatSchedulerSettingDisplay } from './SchedulerSetting/schedulerSettingsDisplay';
import { Edit } from '@mui/icons-material';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import { CircularProgress, Box } from '@mui/material';
import { MAX_DEPARTMENTS } from '../../../module/schedule/utils/scheduleDepartmentUtils';
import { useResetSettingsScroll } from '../utils/resetSettingsScroll';

type ViewMode =
	| 'list'
	| 'createJobCode'
	| 'createTimeOffReason'
	| 'createDepartment'
	| 'createPosition'
	| 'editJobCode'
	| 'editTimeOffReason'
	| 'editDepartment'
	| 'editPosition'
	| 'editGeneralSettings';

const SchedulerSetting = () => {
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
	const canViewScheduler = hasViewPermission('Schedule');
	const canEditScheduler = hasEditPermission('Schedule');
	const canDeleteScheduler = hasDeletePermission('Schedule');
	const canExecuteScheduler = hasExecutePermission('Schedule');

	const [viewMode, setViewMode] = useState<ViewMode>('list');
	useResetSettingsScroll(viewMode);
	const [jobCodes, setJobCodes] = useState<JobCode[]>([]);
	const [departments, setDepartments] = useState<SchedulerDepartment[]>([]);
	const [positions, setPositions] = useState<SchedulerPosition[]>([]);
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [settings, setSettings] = useState<GeneralSettings | null>(null);
	const [editingJobCode, setEditingJobCode] = useState<JobCode | null>(null);
	const [editingDepartment, setEditingDepartment] = useState<SchedulerDepartment | null>(null);
	const [editingPosition, setEditingPosition] = useState<SchedulerPosition | null>(null);
	const [editingTimeOffReason, setEditingTimeOffReason] = useState<TimeOffReason | null>(null);
	
	const [loadingSettings, setLoadingSettings] = useState(false);
	const [loadingJobCodes, setLoadingJobCodes] = useState(false);
	const [loadingDepartments, setLoadingDepartments] = useState(false);
	const [loadingPositions, setLoadingPositions] = useState(false);
	const [loadingTimeOffReasons, setLoadingTimeOffReasons] = useState(false);

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoadingSettings(true);
		setLoadingJobCodes(true);
		setLoadingDepartments(true);
		setLoadingPositions(true);
		setLoadingTimeOffReasons(true);

		const [
			jobCodesResult,
			departmentsResult,
			positionsResult,
			timeOffReasonsResult,
			settingsResult,
		] = await Promise.allSettled([
			getJobCodes(),
			getDepartments(),
			getPositions(),
			schedulerService.getTimeOffReasons(),
			schedulerService.getSettings(),
		]);

		setJobCodes(jobCodesResult.status === 'fulfilled' ? jobCodesResult.value || [] : []);
		setDepartments(
			departmentsResult.status === 'fulfilled' ? departmentsResult.value || [] : []
		);
		setPositions(positionsResult.status === 'fulfilled' ? positionsResult.value || [] : []);
		setTimeOffReasons(
			timeOffReasonsResult.status === 'fulfilled' ? timeOffReasonsResult.value || [] : []
		);
		setSettings(settingsResult.status === 'fulfilled' ? settingsResult.value || null : null);
		setViewMode('list');

		setLoadingSettings(false);
		setLoadingJobCodes(false);
		setLoadingDepartments(false);
		setLoadingPositions(false);
		setLoadingTimeOffReasons(false);
	};

	const handleBackClick = () => {
		setViewMode('list');
		setEditingJobCode(null);
		setEditingDepartment(null);
		setEditingPosition(null);
		setEditingTimeOffReason(null);
	};

	if (!canViewScheduler) {
		return (
			<div className={styles.schedulerSettings}>
				<Card className="card">
					<div style={{ padding: '20px', textAlign: 'center' }}>
						You do not have permission to view scheduler settings.
					</div>
				</Card>
			</div>
		);
	}

	if (viewMode === 'createJobCode') {
		let nextJobNumber = 1;
		if (jobCodes && jobCodes.length > 0) {
			const usedNumbers = jobCodes.map(jc => Number((jc as any).jobNumber)).filter(n => !isNaN(n));
			if (usedNumbers.length > 0) {
				nextJobNumber = Math.max(...usedNumbers) + 1;
			}
		}
		return (
			<div className={styles.schedulerSettings}>
				<CreateJobCode
					backButtonClick={handleBackClick}
					onJobCodeCreated={loadData}
					defaultJobNumber={nextJobNumber}
					departments={departments}
				/>
			</div>
		);
	}

	if (viewMode === 'editJobCode' && editingJobCode) {
		return (
			<div className={styles.schedulerSettings}>
				<EditJobCode
					jobCode={editingJobCode}
					handleBackClick={handleBackClick}
					onJobCodeUpdated={loadData}
					departments={departments}
				/>
			</div>
		);
	}

	if (viewMode === 'createDepartment') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateDepartment
					backButtonClick={handleBackClick}
					onDepartmentCreated={loadData}
					existingDepartments={departments}
				/>
			</div>
		);
	}

	if (viewMode === 'editDepartment' && editingDepartment) {
		return (
			<div className={styles.schedulerSettings}>
				<EditDepartment
					department={editingDepartment}
					handleBackClick={handleBackClick}
					onDepartmentUpdated={loadData}
					existingDepartments={departments}
				/>
			</div>
		);
	}

	if (viewMode === 'createPosition') {
		return (
			<div className={styles.schedulerSettings}>
				<CreatePosition
					backButtonClick={handleBackClick}
					onPositionCreated={loadData}
					jobCodes={jobCodes}
					existingPositions={positions}
				/>
			</div>
		);
	}

	if (viewMode === 'editPosition' && editingPosition) {
		return (
			<div className={styles.schedulerSettings}>
				<EditPosition
					position={editingPosition}
					handleBackClick={handleBackClick}
					onPositionUpdated={loadData}
					jobCodes={jobCodes}
					existingPositions={positions}
				/>
			</div>
		);
	}

	if (viewMode === 'createTimeOffReason') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateTimeOffReason backButtonClick={handleBackClick} onTimeOffReasonCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editTimeOffReason' && editingTimeOffReason) {
		return (
			<div className={styles.schedulerSettings}>
				<EditTimeOffReason
					reason={editingTimeOffReason}
					handleBackClick={handleBackClick}
					onTimeOffReasonUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'editGeneralSettings') {
		return (
			<div className={styles.schedulerSettings}>
				<EditGeneralSettings
					settings={settings}
					handleBackClick={handleBackClick}
					onSettingsUpdated={loadData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>General Settings</h2>
				</div>
				<div className={styles.list}>
					<div className={styles.tableContainer}>
						{loadingSettings ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
								<CircularProgress size={20} color="inherit" />
							</Box>
						) : (
							<table className={styles.workplaceTable}>
								<thead>
									<tr>
										<th>Minor if Less Than</th>
										<th>Overtime Daily Hours</th>
										<th>Overtime Daily Warning</th>
										<th>Time Increment (min)</th>
										<th>Default Shift Length</th>
										<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<span>{formatSchedulerSettingDisplay(settings?.ageOfMinor)}</span>
										</td>
										<td>
											<span>{formatSchedulerSettingDisplay(settings?.overtimeDailyHoursAt, 'h')}</span>
										</td>
										<td>
											<span>{formatSchedulerSettingDisplay(settings?.overtimeDailyWarningAt, 'h')}</span>
										</td>
										<td>
											<span>{formatSchedulerSettingDisplay(settings?.timeIncrement, 'min')}</span>
										</td>
										<td>
											<span>{formatSchedulerSettingDisplay(settings?.defaultShiftLengthHours, 'h')}</span>
										</td>
										<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
											<button
												className={styles.iconButton}
											title={canEditScheduler ? "Edit" : "You do not have permission to edit"}
											onClick={() => {
												setViewMode('editGeneralSettings');
											}}
											disabled={!canEditScheduler}
											style={{ opacity: canEditScheduler ? 1 : 0.5, cursor: canEditScheduler ? 'pointer' : 'not-allowed' }}
											>
												<Edit className={styles.editIcon} />
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Departments</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createDepartment')}
						disabled={!canExecuteScheduler || departments.length >= MAX_DEPARTMENTS}
						title={
							!canExecuteScheduler
								? 'You do not have permission to add departments'
								: departments.length >= MAX_DEPARTMENTS
									? `Maximum of ${MAX_DEPARTMENTS} departments allowed`
									: 'Add Department'
						}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingDepartments ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<DepartmentsList
							departments={departments}
							jobCodes={jobCodes}
							onDepartmentChanged={loadData}
							onEditClick={(department) => {
								setEditingDepartment(department);
								setViewMode('editDepartment');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Job Codes</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createJobCode')}
						disabled={!canExecuteScheduler}
						title={canExecuteScheduler ? "Add Job Code" : "You do not have permission to add job codes"}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingJobCodes ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<JobCodesList
							jobCodes={jobCodes}
							departments={departments}
							onJobCodeCreated={loadData}
							onEditClick={(jobCode) => {
								setEditingJobCode(jobCode);
								setViewMode('editJobCode');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Positions</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createPosition')}
						disabled={!canExecuteScheduler || jobCodes.length === 0}
						title={
							!canExecuteScheduler
								? 'You do not have permission to add positions'
								: jobCodes.length === 0
									? 'Create a job code before adding positions'
									: 'Add Position'
						}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingPositions ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<PositionsList
							positions={positions}
							jobCodes={jobCodes}
							onPositionChanged={loadData}
							onEditClick={(position) => {
								setEditingPosition(position);
								setViewMode('editPosition');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Time Off Reasons</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createTimeOffReason')}
						disabled={!canExecuteScheduler}
						title={canExecuteScheduler ? "Add Time Off Reason" : "You do not have permission to add time off reasons"}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingTimeOffReasons ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<TimeOffReasonsList
							timeOffReasons={timeOffReasons}
							onTimeOffReasonCreated={loadData}
							onEditClick={(reason) => {
								setEditingTimeOffReason(reason);
								setViewMode('editTimeOffReason');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>
		</div>
	);
};

export default SchedulerSetting;
